<template>
  <form class="page-container" @submit.prevent="addOrUpdatePlan">
    <h2 v-if="editMode" class="text-center">Edit Subscription Plan</h2>
    <h2 v-else class="text-center">Add New Subscription Plan</h2>
    <div>
      <label for="cohort-id">Cohort Id</label>
      <VueSelect
        v-model="cohort_name"
        placeholder="Select Cohort"
        label="name"
        :options="cohortList"
        :reduce="cohort => cohort.name"
        :clearable="false"
        required
        @change="handleChange"
      />
      <small v-if="cohort_name"
        >Base Price: {{ cohortBasePriceInRupees }}</small
      >
    </div>
    <div>
      <label for="name">Plan Name</label>
      <input
        id="name"
        v-model.trim="name"
        required
        placeholder="plan name"
        type="text"
        minlength="3"
      />
    </div>
    <div>
      <label for="name">Description</label>
      <textarea
        id="description"
        v-model.trim="description"
        required
        placeholder="plan description"
        maxlength="500"
      ></textarea>
    </div>
    <div>
      <label for="enable">Enabled?</label>
      <input id="enable" v-model="active" type="checkbox" />
    </div>
    <div class="group">
      <div>
        <label for="quantity">Installments</label>
        <input
          id="quantity"
          v-model.number="quantity"
          type="number"
          name="quantity"
          min="1"
          max="99"
          required
        />
      </div>
      <div>
        <label for="interval">Interval</label>
        <input
          id="interval"
          v-model.number="interval"
          type="number"
          name="interval"
          min="1"
          max="99"
          required
        />
      </div>
      <div>
        <label for="period">Period</label>
        <select id="period" v-model="period" name="period" required>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>
    </div>
    <div>
      <blockquote v-if="planAmount">
        {{ planHumanReadableDescription }}
      </blockquote>
    </div>
    <div v-if="razorpay_plan_id">
      <label for="razorpay_plan_id">Razorpay Plan Id</label>
      <input
        id="razorpay_plan_id"
        type="text"
        name="razorpay_plan_id"
        disabled
        :value="razorpay_plan_id"
      />
    </div>
    <div>
      <button
        v-if="!updateInProgress"
        type="submit"
        :disabled="updateInProgress"
      >
        {{ editMode ? "Update" : "Add" }} Plan
      </button>
      <button v-else :disabled="updateInProgress">...submitting</button>
    </div>
  </form>
</template>

<script>
import VueSelect from "vue-select"
import siteConfig from "@/utils/siteConfig"
import { formatAsRupees } from "@/utils/currency"

export default {
  components: {
    VueSelect,
  },

  data() {
    return {
      currency: "INR",
      trial_expires_in: undefined,
      updated_on: undefined,
      created_on: undefined,
      name: undefined,
      interval: 1,
      quantity: 1,
      period: "monthly",
      id: undefined,
      description: undefined,
      coupon_id: undefined,
      coupon: {},
      expires_in: undefined,
      notes: undefined,
      cohort_id: undefined,
      cohort: {},
      active: true,
      razorpay_plan_id: undefined,
      // additional
      cohort_name: undefined,
      cohort_base_price: undefined,
      cohortList: [],
      editMode: false,
      updateInProgress: false,
    }
  },

  computed: {
    planBillingFrequencyDescription() {
      if (!this.period || !this.interval) return ""
      if (this.interval < 1) return ""

      const unit = {
        monthly: "month",
        daily: "day",
        weekly: "week",
        yearly: "year",
      }[this.period]

      if (this.interval === 1) {
        return unit
      }
      // if (this.interval > 1)
      return `${this.interval} ${unit}s`
    },
    planBillingQuantityDescription() {
      if (this.quantity === 1) return `1 installment`
      return `${this.quantity} installements`
    },
    planAmount() {
      if (this.quantity < 1) return NaN
      return this.cohort_base_price / this.quantity
    },
    cohortBasePriceInRupees() {
      return formatAsRupees(this.cohort_base_price)
    },
    planInstallmentAmountDescription() {
      return formatAsRupees(this.planAmount)
    },
    planHumanReadableDescription() {
      return `${this.planBillingQuantityDescription} of ${this.planInstallmentAmountDescription} each, payable every ${this.planBillingFrequencyDescription}`
    },
  },

  created() {
    if (this.$route.params && this.$route.params.id) {
      this.editMode = true
      this.fetchPlan()
    } else {
      this.getPaidCohorts()
    }
  },

  methods: {
    async fetchPlan() {
      const loader = this.$loading.show()
      const plan_id = this.$route.params.id
      try {
        const res = await this.axios({
          method: "get",
          url: `${siteConfig.siteURL}admin/subscription/plans/${plan_id}`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
        })
        const { item: plan } = res.data
        Object.assign(this, plan)
        await this.getPaidCohorts()
      } catch (err) {
        this.$noty.error(`Cannot edit plan with id ${plan_id}\n\n${err}`)
      } finally {
        loader.hide()
      }
    },
    async getPaidCohorts() {
      try {
        const res = await this.axios({
          method: "get",
          url: `${siteConfig.siteURL}cohorts/?pagination=false`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
        })

        this.cohortList = res.data.items.filter(c => c.base_price > 0)
        if (this.editMode) {
          const cohort = this.cohortList.find(c => c.id === this.cohort_id)
          if (cohort) {
            this.cohort_id = cohort.id
            this.cohort_name = cohort.name
            this.cohort_base_price = cohort.base_price
          }
        }
      } catch (err) {
        this.$noty.error(
          "Could not fetch the list of cohorts, please refresh again in some time."
        )
      }
    },
    addOrUpdatePlan() {
      if (!this.cohort_id) {
        this.$noty.error("Please attach the plan to a cohort before saving it")
        return
      }

      let url = `${siteConfig.siteURL}admin/subscription/plans`
      let method = "POST"
      const data = {
        cohort_id: this.cohort_id,
        name: this.name,
        description: this.description,
        active: this.active,
        period: this.period,
        interval: this.interval,
        quantity: this.quantity,
        currency: "INR",
        // not needed as of now
        expires_in: 1,
        trial_expires_in: 1,
      }

      if (this.editMode) {
        url = `${siteConfig.siteURL}admin/subscription/plans/${this.id}`
        method = "PUT"
        data.id = this.id
      }

      this.updateInProgress = true
      this.axios({
        method,
        url,
        data,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.$noty.success(res.data.message)
          setTimeout(() => {
            this.$router.push({ name: "PlanList" })
          }, 1000)
        })
        .catch(err => {
          this.$noty.error(`Could not add/update plan:\n\n${err}`)
        })
        .finally(() => {
          this.updateInProgress = false
        })
    },
    handleChange(option) {
      if (option && typeof option === "object") {
        this.cohort_id = option.id
        this.cohort_name = option.name
        this.cohort_base_price = option.base_price
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.page-container {
  width: 100%;
  max-width: 40rem;
  margin: auto;
}

h2 {
  @apply mb-12;
}

form > * + * {
  @apply my-6;
}

label {
  @apply p-0 text-grey-darker text-xs;
}

label + * {
  @apply m-0 mt-1;
}

input[type="text"],
input[type="number"],
textarea,
select {
  @apply border border-grey rounded w-full p-2;
}

input[disabled] {
  @apply bg-grey-lighter text-black;
}

select {
  padding: 0 0.25rem;
}

button {
  @apply border bg-green text-white font-bold px-6 py-2 rounded-full;
}

button:hover,
button:focus {
  @apply bg-green-dark;
}

.group {
  @apply flex;
}

.group > * + * {
  @apply ml-8;

  min-width: 6rem;
}

blockquote {
  @apply border-l-4 pl-4 py-4 bg-grey-lightest border-blue-lighter;
}
</style>
